
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PollWidget from "@/components/polls/PollWidget";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useCountry } from "@/contexts/country";
import { usePollFetch } from "@/hooks/polls/usePollFetch";
import PollsFilters, { PollFilters } from "@/components/polls/PollsFilters";
import PollsList from "@/components/polls/PollsList";
import PollsInfiniteList from "@/components/polls/PollsInfiniteList";
import { useFilteredPolls } from "@/hooks/polls/useFilteredPolls";
import { useInfinitePolls } from "@/hooks/polls/useInfinitePolls";
import PollsHeader from "@/components/polls/PollsHeader";

const Polls = () => {
  const { countryCode, availableCountries } = useCountry();
  const [filters, setFilters] = useState<PollFilters>({
    status: 'active',
    country: countryCode,
    constituency: null,
    category: null,
    year: null,
    search: '',
  });
  
  const [sortBy, setSortBy] = useState<{field: string, direction: 'asc' | 'desc'}>({
    field: 'created_at',
    direction: 'desc'
  });
  
  const { polls, isLoading } = usePollFetch({
    includeAllStatuses: true
  });

  const { filteredPolls, activeFiltersCount } = useFilteredPolls({
    polls,
    status: filters.status,
    countryCode: filters.country,
    constituencyCode: filters.constituency,
    category: filters.category,
    year: filters.year,
    search: filters.search,
  });
  
  const { 
    displayedItems,
    hasMore,
    isLoading: isLoadingMore,
    loadMore,
    lastItemRef,
    totalItems
  } = useInfinitePolls({ 
    items: filteredPolls,
    itemsPerPage: 10
  });

  const handleFilterChange = (newFilters: PollFilters) => {
    setFilters(newFilters);
  };
  
  const handleSortChange = (sort: {field: string, direction: 'asc' | 'desc'}) => {
    setSortBy(sort);
  };

  // Get country name for breadcrumbs if country filter is active
  const getCountryName = () => {
    if (!filters.country) return undefined;
    const country = availableCountries.find(c => c.code === filters.country);
    return country ? country.name : undefined;
  };

  useEffect(() => {
    document.title = "Polls | NationCard™";
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      
      <main className="container mx-auto px-4 py-20 flex-1">
        <div className="max-w-5xl mx-auto">
          <PollsHeader countryName={getCountryName()} />
          
          <h1 className="text-3xl font-bold mb-8 text-nation-blue dark:text-blue-300">
            Polls and Citizen Opinions
          </h1>
          
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Active Polls</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Participate in our active polls and make your voice heard. Your responses help shape our nation's insight cards.
              </p>
              
              <PollWidget />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Browse All Polls</CardTitle>
            </CardHeader>
            <CardContent>
              <PollsFilters 
                onFilterChange={handleFilterChange} 
                onSortChange={handleSortChange}
                activeFiltersCount={activeFiltersCount}
              />
              
              {isLoading ? (
                <div className="py-8 text-center">
                  <div className="animate-spin h-8 w-8 border-4 border-nation-blue border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading polls...</p>
                </div>
              ) : (
                <>
                  <PollsInfiniteList 
                    polls={displayedItems} 
                    emptyMessage="No polls found matching your criteria. Try adjusting your filters."
                    hasMore={hasMore}
                    isLoading={isLoadingMore}
                    loadMore={loadMore}
                    lastItemRef={lastItemRef}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Polls;
